// components/MapPreview.js - Web version using Leaflet
import React, { useEffect, useRef } from 'react';
import { FiNavigation, FiMapPin } from 'react-icons/fi';
import '../styles/MapPreview.scss';

const MapPreview = ({ location, name, address, height = 200, showDirectionsButton = true }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    if (!location || !mapRef.current) return;
    // Guard against location objects with missing or null coordinates
    if (location.lat == null || location.lng == null) return;

    let cancelled = false;

    // Load Leaflet CSS
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }

    function initMap() {
      if (cancelled || !mapRef.current) return;

      // Destroy any existing instance and clear Leaflet's container marker
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      // Leaflet sets _leaflet_id on the container; clear it so re-init works
      delete mapRef.current._leaflet_id;

      const map = window.L.map(mapRef.current, {
        dragging: false,
        touchZoom: false,
        scrollWheelZoom: false,
        doubleClickZoom: false,
        boxZoom: false,
        keyboard: false,
        zoomControl: false,
        attributionControl: false,
      }).setView([location.lat, location.lng], 15);

      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
      }).addTo(map);

      const icon = window.L.icon({
        iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
        iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41],
      });

      window.L.marker([location.lat, location.lng], { icon }).addTo(map);

      mapInstanceRef.current = map;
    }

    if (!window.L) {
      // Avoid duplicate script tags
      if (!document.getElementById('leaflet-js')) {
        const script = document.createElement('script');
        script.id = 'leaflet-js';
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = initMap;
        document.body.appendChild(script);
      } else {
        // Script tag exists but L not ready yet — wait for load
        document.getElementById('leaflet-js').addEventListener('load', initMap, { once: true });
      }
    } else {
      initMap();
    }

    return () => {
      cancelled = true;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [location]);

  const handleGetDirections = () => {
    if (!location) return;
    
    const query = address || `${location.lat},${location.lng}`;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(query)}`;
    window.open(url, '_blank');
  };

  if (!location) {
    return (
      <div className="map-preview-placeholder" style={{ height }}>
        <FiMapPin size={48} />
        <p>No location set</p>
      </div>
    );
  }

  return (
    <div className="map-preview-container">
      <div className="map-wrapper" style={{ height }}>
        <div ref={mapRef} className="map-element" />
      </div>

      <div className="map-info">
        <div className="location-details">
          <h4>{name || 'Event Location'}</h4>
          {address && <p>{address}</p>}
        </div>
        
        {showDirectionsButton && (
          <button className="directions-btn" onClick={handleGetDirections}>
            <FiNavigation />
            <span>Get Directions</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default MapPreview;